import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fundReceipts, type FundDebtResult } from '@/api/fund_receipts';
import { orgs } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const now = new Date();
const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface RowState {
  sick_calculation_id: string | null;
  employee_id: string | null;
  amount: string;
  // Підписи (з fund-debt або '—' для рядків збереженого документа).
  label?: string;
  accrued?: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  comment: string;
  items: RowState[];
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: now.toISOString().slice(0, 16),
  period_year: now.getFullYear(),
  period_month: now.getMonth() + 1,
  comment: '',
  items: [],
};

export default function FundReceiptEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['fund-receipt', id],
    queryFn: () => fundReceipts.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // Лікарняні з невідшкодованим боргом Фонду за період — для «На основі нарахування».
  const debtQ = useQuery<FundDebtResult>({
    queryKey: ['fund-debt', form.organization_id, form.period_year, form.period_month],
    queryFn: () => fundReceipts.fundDebt({
      organization_id: form.organization_id,
      period_year: form.period_year, period_month: form.period_month,
    }),
    enabled: !!form.organization_id && !isPosted,
  });
  // Ще не додані у рядки документа.
  const available = useMemo(() => {
    const used = new Set(form.items.map((r) => r.sick_calculation_id));
    return (debtQ.data?.items ?? []).filter((d) => !used.has(d.sick_calculation_id));
  }, [debtQ.data, form.items]);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        period_year: d.period_year,
        period_month: d.period_month,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          sick_calculation_id: it.sick_calculation_id,
          employee_id: it.employee_id,
          amount: String(it.amount),
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  // Підписи рядків збереженого документа — з fund-debt того ж періоду
  // (шукаємо по sick_calculation_id; вже відшкодовані там відсутні → лишаємо '—').
  const labelOf = useMemo(() => {
    const m = new Map<string, { label: string; accrued: string }>();
    (debtQ.data?.items ?? []).forEach((d) => m.set(d.sick_calculation_id, {
      label: `${d.document_number ?? ''} — ${d.employee_name ?? ''}`,
      accrued: String(d.accrued ?? ''),
    }));
    return m;
  }, [debtQ.data]);

  function addFromAccrual(calcId: string) {
    const d = debtQ.data?.items.find((x) => x.sick_calculation_id === calcId);
    if (!d) return;
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        sick_calculation_id: d.sick_calculation_id,
        employee_id: d.employee_id,
        amount: String(d.amount),  // залишок до відшкодування
        label: `${d.document_number ?? ''} — ${d.employee_name ?? ''}`,
        accrued: String(d.accrued ?? ''),
      }],
    }));
  }
  function updRow(i: number, amount: string) {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], amount } as RowState;
      return { ...f, items };
    });
  }
  function removeRow(i: number) {
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  }

  const total = useMemo(
    () => form.items.reduce((s, it) => s + Number(it.amount || 0), 0),
    [form.items]);

  // Update-схема backend'а — extra=forbid БЕЗ organization_id/fund_marker
  // (незмінні після створення) → на PATCH їх НЕ шлемо, інакше 422.
  function payload() {
    return {
      document_number: form.document_number || undefined,
      document_date: form.document_date,
      period_year: form.period_year,
      period_month: form.period_month,
      comment: form.comment || null,
      items: form.items
        .filter((it) => Number(it.amount) > 0)
        .map((it) => ({
          sick_calculation_id: it.sick_calculation_id,
          employee_id: it.employee_id,
          amount: it.amount,
        })),
    };
  }
  const createPayload = () => ({
    organization_id: form.organization_id, fund_marker: 'PFU', ...payload(),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return fundReceipts.create(createPayload());
      return fundReceipts.update(id!, payload());
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['fund-receipts'] });
      if (isNew) { qc.setQueryData(['fund-receipt', doc.id], doc); navigate(`/fund-receipts/${doc.id}`); }
      else qc.invalidateQueries({ queryKey: ['fund-receipt', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });

  const saveAndPost = useMutation({
    mutationFn: async () => {
      const doc = isNew
        ? await fundReceipts.create(createPayload())
        : await fundReceipts.update(id!, payload());
      await fundReceipts.post(doc.id);
      return fundReceipts.get(doc.id);
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['fund-receipts'] });
      qc.setQueryData(['fund-receipt', doc.id], doc);
      navigate(`/fund-receipts/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });

  const unpost = useMutation({
    mutationFn: async () => {
      const fresh = await fundReceipts.unpost(id!);
      qc.setQueryData(['fund-receipt', id], fresh);
      return fresh;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fund-receipts'] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });

  const remove = useMutation({
    mutationFn: () => fundReceipts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-receipts'] });
      navigate('/fund-receipts');
    },
  });

  const canSave = !!form.organization_id && !!form.document_date && !isPosted;

  return (
    <div className="card">
      {/* Шапка: заголовок зліва, кнопки СПРАВА (стандарт editor'ів документів). */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове надходження від Фонду' : `Надходження ${form.document_number}`}
        </h2>
        <DocumentToolbar
          isNew={isNew}
          onBackToList={() => navigate('/fund-receipts')}
          onSave={() => save.mutate()}
          saveDisabled={!canSave}
          savePending={save.isPending || saveAndPost.isPending}
          isPosted={isPosted}
          onPost={() => saveAndPost.mutate()}
          onUnpost={() => unpost.mutate()}
          postPending={saveAndPost.isPending}
          unpostPending={unpost.isPending}
          deletable={!isNew}
          onDelete={() => { if (confirm('Видалити документ?')) remove.mutate(); }}
          deletePending={remove.isPending}
        />
      </div>
      <p style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: 0 }}>
        Гроші Фонду (ПФУ) по лікарняних на спецрахунок. Оберіть нарахування
        лікарняного — рядок заповниться залишком до відшкодування.
      </p>

      {isPosted && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>
          ✓ проведено.
        </div>
      )}
      {error && (
        <div style={{ background: '#fee', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      {/* Шапка */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value, items: [] })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ minWidth: 160 }}>
          <label>Дата *</label>
          <DatePopover value={form.document_date.slice(0, 10)}
            onChange={(v) => setForm({ ...form, document_date: `${v}T00:00` })}
            disabled={isPosted} />
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={form.period_month} disabled={isPosted}
              onChange={(e) => setForm({ ...form, period_month: Number(e.target.value) })}
              style={{ flex: 2 }}>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={form.period_year} disabled={isPosted}
              onChange={(e) => setForm({ ...form, period_year: Number(e.target.value) })}
              style={{ flex: 1 }}>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        {!isPosted && (
          <div className="field" style={{ minWidth: 340 }}>
            <label>На основі нарахування (лікарняні з боргом Фонду)</label>
            <select value=""
              onChange={(e) => { if (e.target.value) addFromAccrual(e.target.value); }}
              disabled={!form.organization_id}>
              <option value="">
                {!form.organization_id ? '— спершу оберіть організацію —'
                  : debtQ.isFetching ? '— завантаження…'
                  : available.length === 0
                    ? '— немає невідшкодованих лікарняних за період —'
                    : '— оберіть лікарняний (додасться рядком) —'}
              </option>
              {available.map((d) => (
                <option key={d.sick_calculation_id} value={d.sick_calculation_id}>
                  {d.document_number} — {d.employee_name} — залишок {fmt2(d.amount)} грн
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Рядки */}
      <table style={{ marginTop: '1rem', width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem' }}>Нарахування лікарняного</th>
            <th style={{ padding: '0.5rem', width: 140, textAlign: 'right' }}>Нараховано (ПФУ)</th>
            <th style={{ padding: '0.5rem', width: 140, textAlign: 'right' }}>Сума надходження</th>
            <th style={{ padding: '0.5rem', width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const info = it.label
              ? { label: it.label, accrued: it.accrued ?? '' }
              : labelOf.get(it.sick_calculation_id ?? '') ?? { label: '—', accrued: '' };
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.4rem' }}>{info.label}</td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                  {info.accrued ? fmt2(info.accrued) : '—'}
                </td>
                <td style={{ padding: '0.4rem', textAlign: 'right' }}>
                  {isPosted ? fmt2(it.amount) : (
                    <input type="number" step="0.01" value={it.amount}
                      style={{ width: '100%', textAlign: 'right' }}
                      onChange={(e) => updRow(i, e.target.value)} />
                  )}
                </td>
                <td style={{ padding: '0.4rem' }}>
                  {!isPosted && (
                    <button className="danger" style={{ padding: '0.2rem 0.5rem' }}
                      onClick={() => removeRow(i)}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1' }}>
            <td colSpan={2} style={{ padding: '0.5rem', fontWeight: 600 }}>Разом</td>
            <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{fmt2(total)}</td>
            <td />
          </tr>
        </tfoot>
      </table>

      {form.items.length === 0 && (
        <p style={{ color: 'var(--muted)', marginTop: '0.75rem' }}>
          Оберіть нарахування лікарняного вгорі — воно додасться рядком із
          залишком до відшкодування.
        </p>
      )}
    </div>
  );
}
